export interface RelationColumnData {
  targetDocId?: string;
  targetDatabaseId: string;
  targetFieldId?: string;
  cardinality?: string;
  inverseFieldId?: string;
  inverseCardinality?: string;
}

export interface DatabaseColumn {
  id: string;
  type: string;
  name: string;
  data?: RelationColumnData;
}

export interface DatabaseHostModelLike {
  id: string;
  props: Record<string, unknown>;
}

export interface DatabaseMovedEventDetail {
  databaseId: string;
  oldDocId: string;
  newDocId: string;
  oldBlockId: string;
  newBlockId: string;
}

export function getCanonicalDatabaseId(model: DatabaseHostModelLike): string {
  const propDatabaseId = model.props.databaseId;
  return typeof propDatabaseId === 'string' && propDatabaseId.length > 0
    ? propDatabaseId
    : model.id;
}

export function updateIncomingRelationColumnsAfterDatabaseMove(
  columns: readonly DatabaseColumn[],
  movedDatabaseId: string,
  newDocId: string
): { columns: DatabaseColumn[]; updatedColumnIds: string[] } {
  const updatedColumnIds: string[] = [];
  const updatedColumns = columns.map(column => {
    if (
      column.type !== 'relation' ||
      column.data?.targetDatabaseId !== movedDatabaseId
    ) {
      return cloneColumn(column);
    }

    updatedColumnIds.push(column.id);
    return {
      ...cloneColumn(column),
      data: {
        ...column.data,
        targetDatabaseId: movedDatabaseId,
        targetDocId: newDocId,
      },
    };
  });

  return { columns: updatedColumns, updatedColumnIds };
}

export function updateOutgoingRelationColumnsAfterDatabaseMove(
  columns: readonly DatabaseColumn[],
  movedDatabaseId: string,
  oldDocId: string
): { columns: DatabaseColumn[]; updatedColumnIds: string[] } {
  const updatedColumnIds: string[] = [];
  const updatedColumns = columns.map(column => {
    if (
      column.type !== 'relation' ||
      !column.data ||
      column.data.targetDatabaseId === movedDatabaseId
    ) {
      return cloneColumn(column);
    }

    const relationWasImplicitOldDoc =
      !column.data.targetDocId || column.data.targetDocId === oldDocId;
    if (!relationWasImplicitOldDoc) {
      return cloneColumn(column);
    }

    updatedColumnIds.push(column.id);
    return {
      ...cloneColumn(column),
      data: {
        ...column.data,
        targetDocId: oldDocId,
      },
    };
  });

  return { columns: updatedColumns, updatedColumnIds };
}

export function createDatabaseMovedEventDetail(input: {
  databaseId: string;
  oldDocId: string;
  newDocId: string;
  oldBlockId: string;
  newBlockId: string;
}): DatabaseMovedEventDetail {
  return {
    databaseId: input.databaseId,
    oldDocId: input.oldDocId,
    newDocId: input.newDocId,
    oldBlockId: input.oldBlockId,
    newBlockId: input.newBlockId,
  };
}

function cloneColumn(column: DatabaseColumn): DatabaseColumn {
  return {
    ...column,
    data: column.data ? { ...column.data } : undefined,
  };
}
